import { Users, Calendar, Bell, CheckCircle, AlertTriangle, TrendingUp, ArrowRight } from 'lucide-react';
import { useAuthStore } from '../../stores/auth.store';

// Mock data matching wireframes
const criticalAlerts = [
  {
    id: '1',
    title: 'Staffing Gap: Tuesday 2-5pm',
    description: '3 agents short. SLA at risk: 85% vs target 90%',
    recommendation: 'Hire 2 FTE + 1 temp for Q4',
    severity: 'critical' as const,
  },
  {
    id: '2',
    title: 'Team A Adherence: 75%',
    description: 'Below target of 90%. 5 agents with repeated late starts',
    recommendation: 'Review break policies, coaching needed',
    severity: 'warning' as const,
  },
];

const keyMetrics = [
  { title: 'Agents Online', value: '142/150', change: '+5 from yesterday', icon: Users, color: 'blue' },
  { title: 'Avg Adherence', value: '87.5%', change: '-2.3% vs target', icon: CheckCircle, color: 'green', isNegative: true },
  { title: 'Open Shifts', value: '12', change: 'Next 7 days', icon: Calendar, color: 'amber' },
  { title: 'Pending Actions', value: '8', change: 'Requires approval', icon: Bell, color: 'purple' },
];

const quickActions = [
  { label: 'Create New Schedule', icon: Calendar },
  { label: 'Run Forecast', icon: TrendingUp },
  { label: 'Approve Time-off Requests (3)', icon: CheckCircle },
  { label: 'Review Shift Swaps (2)', icon: Users },
];

const weekSchedule = [
  { day: 'Monday', scheduled: 145, status: 'ok' },
  { day: 'Tuesday', scheduled: 145, status: 'warning', variance: -3 },
  { day: 'Wednesday', scheduled: 145, status: 'ok' },
  { day: 'Thursday', scheduled: 145, status: 'ok' },
  { day: 'Friday', scheduled: 145, status: 'ok' },
];

// Coverage heat map data
const channels = [
  { name: 'Phone Support', icon: '📞', agents: 52, color: 'blue', weeklyTotal: 4832, unit: 'calls' },
  { name: 'Email Support', icon: '✉️', agents: 28, color: 'purple', weeklyTotal: 2000, unit: 'emails' },
  { name: 'Live Chat', icon: '💬', agents: 35, color: 'green', weeklyTotal: 3232, unit: 'chats' },
  { name: 'Social Media', icon: '📱', agents: 18, color: 'pink', weeklyTotal: 1712, unit: 'messages' },
  { name: 'Technical Support', icon: '🔧', agents: 25, color: 'amber', weeklyTotal: 1460, unit: 'tickets' },
];

const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const coverageData = {
  'Phone Support': [
    { status: 'green', expected: 682, actual: 695 },
    { status: 'red', expected: 734, actual: 812 },
    { status: 'red', expected: 721, actual: 798 },
    { status: 'green', expected: 695, actual: 701 },
    { status: 'amber', expected: 708, actual: 752 },
    { status: 'green', expected: 512, actual: 508 },
    { status: 'blue', expected: 398, actual: 366 },
  ],
  'Email Support': [
    { status: 'green', expected: 298, actual: 305 },
    { status: 'green', expected: 315, actual: 312 },
    { status: 'amber', expected: 322, actual: 348 },
    { status: 'green', expected: 308, actual: 301 },
    { status: 'green', expected: 318, actual: 315 },
    { status: 'blue', expected: 245, actual: 218 },
    { status: 'green', expected: 198, actual: 201 },
  ],
  'Live Chat': [
    { status: 'green', expected: 445, actual: 452 },
    { status: 'green', expected: 478, actual: 471 },
    { status: 'green', expected: 485, actual: 489 },
    { status: 'amber', expected: 472, actual: 512 },
    { status: 'green', expected: 468, actual: 465 },
    { status: 'green', expected: 358, actual: 361 },
    { status: 'green', expected: 285, actual: 282 },
  ],
  'Social Media': [
    { status: 'green', expected: 245, actual: 251 },
    { status: 'green', expected: 268, actual: 265 },
    { status: 'green', expected: 272, actual: 278 },
    { status: 'green', expected: 265, actual: 262 },
    { status: 'green', expected: 258, actual: 255 },
    { status: 'amber', expected: 215, actual: 242 },
    { status: 'blue', expected: 178, actual: 159 },
  ],
  'Technical Support': [
    { status: 'green', expected: 215, actual: 218 },
    { status: 'amber', expected: 232, actual: 254 },
    { status: 'amber', expected: 228, actual: 248 },
    { status: 'green', expected: 221, actual: 225 },
    { status: 'green', expected: 225, actual: 222 },
    { status: 'blue', expected: 178, actual: 162 },
    { status: 'blue', expected: 145, actual: 131 },
  ],
};

export default function DashboardPage() {
  const { user } = useAuthStore();

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome back, {user?.firstName || 'there'}</h1>
        <p className="text-gray-600">Here's what's happening with your team today</p>
      </div>

      {/* Critical Alerts */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">🚨 Critical Alerts</h2>
        <div className="space-y-3">
          {criticalAlerts.map((alert) => (
            <div
              key={alert.id}
              className={`${
                alert.severity === 'critical'
                  ? 'bg-red-50 border-l-4 border-red-500'
                  : 'bg-amber-50 border-l-4 border-amber-500'
              } p-4 rounded-lg`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start">
                  <AlertTriangle
                    className={`w-5 h-5 ${
                      alert.severity === 'critical' ? 'text-red-600' : 'text-amber-600'
                    } mt-0.5 mr-3`}
                  />
                  <div>
                    <h3
                      className={`font-semibold ${
                        alert.severity === 'critical' ? 'text-red-900' : 'text-amber-900'
                      }`}
                    >
                      {alert.title}
                    </h3>
                    <p
                      className={`${
                        alert.severity === 'critical' ? 'text-red-700' : 'text-amber-700'
                      } text-sm mt-1`}
                    >
                      {alert.description}
                    </p>
                    <p
                      className={`${
                        alert.severity === 'critical' ? 'text-red-600' : 'text-amber-600'
                      } text-sm mt-2 font-medium`}
                    >
                      💡 Recommendation: {alert.recommendation}
                    </p>
                  </div>
                </div>
                <button
                  className={`px-4 py-2 ${
                    alert.severity === 'critical'
                      ? 'bg-red-600 hover:bg-red-700'
                      : 'bg-amber-600 hover:bg-amber-700'
                  } text-white rounded-lg text-sm font-medium`}
                >
                  {alert.severity === 'critical' ? 'Take Action' : 'Review'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-4 gap-6">
        {keyMetrics.map((metric) => (
          <div key={metric.title} className="bg-white border-2 border-gray-200 rounded-xl p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600 text-sm font-medium">{metric.title}</span>
              <metric.icon
                className={`w-5 h-5 ${
                  metric.color === 'blue'
                    ? 'text-blue-600'
                    : metric.color === 'green'
                    ? 'text-green-600'
                    : metric.color === 'amber'
                    ? 'text-amber-600'
                    : 'text-purple-600'
                }`}
              />
            </div>
            <div className="text-3xl font-bold text-gray-900">{metric.value}</div>
            <div className={`text-sm mt-2 ${metric.isNegative ? 'text-red-600' : 'text-green-600'}`}>
              {metric.change}
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions & Schedule Preview */}
      <div className="grid grid-cols-2 gap-6">
        <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="space-y-3">
            {quickActions.map((action) => (
              <button
                key={action.label}
                className="w-full flex items-center justify-between p-3 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all"
              >
                <span className="font-medium text-gray-700">{action.label}</span>
                <ArrowRight className="w-5 h-5 text-gray-400" />
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">This Week's Schedule</h3>
          <div className="space-y-3">
            {weekSchedule.map((day) => (
              <div key={day.day} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="font-medium text-gray-700">{day.day}</span>
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-gray-600">{day.scheduled} scheduled</span>
                  <span
                    className={`text-sm font-medium ${
                      day.status === 'warning' ? 'text-red-600' : 'text-green-600'
                    }`}
                  >
                    {day.status === 'warning' ? `⚠️ ${day.variance}` : '✓'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Coverage Heat Map */}
      <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Coverage Status by Channel</h3>
            <p className="text-sm text-gray-600 mt-1">Past 7 days - Expected vs Actual Volume</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-3 bg-gray-50 px-4 py-2 rounded-lg">
              <div className="flex items-center space-x-1.5">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <span className="text-xs text-gray-600">Under-staffed</span>
              </div>
              <div className="flex items-center space-x-1.5">
                <div className="w-3 h-3 bg-amber-400 rounded-full"></div>
                <span className="text-xs text-gray-600">Low Coverage</span>
              </div>
              <div className="flex items-center space-x-1.5">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="text-xs text-gray-600">Optimal</span>
              </div>
              <div className="flex items-center space-x-1.5">
                <div className="w-3 h-3 bg-blue-400 rounded-full"></div>
                <span className="text-xs text-gray-600">Over-staffed</span>
              </div>
            </div>
          </div>
        </div>

        {/* Heat Map Table */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className="text-left text-sm font-semibold text-gray-700 pb-4 pr-6 w-40">Channel</th>
                {weekDays.map((day, idx) => (
                  <th key={day} className="text-center text-xs font-medium text-gray-600 pb-4 px-3">
                    <div>{day}</div>
                    <div className="text-gray-500 font-normal">Jan {8 + idx}</div>
                  </th>
                ))}
                <th className="text-right text-sm font-semibold text-gray-700 pb-4 pl-6">Total Volume</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {channels.map((channel) => (
                <tr key={channel.name} className="hover:bg-gray-50 transition-colors">
                  <td className="py-4 pr-6">
                    <div className="flex items-center space-x-3">
                      <div
                        className={`w-10 h-10 bg-${channel.color}-100 rounded-lg flex items-center justify-center flex-shrink-0`}
                      >
                        <span className="text-lg">{channel.icon}</span>
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-gray-900">{channel.name}</div>
                        <div className="text-xs text-gray-500">{channel.agents} agents</div>
                      </div>
                    </div>
                  </td>
                  {coverageData[channel.name as keyof typeof coverageData].map((day, idx) => (
                    <td key={idx} className="px-3 py-4">
                      <div className="flex flex-col items-center space-y-1">
                        <div
                          className={`w-8 h-8 rounded-full cursor-pointer transition-all hover:scale-110 ${
                            day.status === 'red'
                              ? 'bg-red-500 hover:ring-4 hover:ring-red-200'
                              : day.status === 'amber'
                              ? 'bg-amber-400 hover:ring-4 hover:ring-amber-200'
                              : day.status === 'green'
                              ? 'bg-green-500 hover:ring-4 hover:ring-green-200'
                              : 'bg-blue-400 hover:ring-4 hover:ring-blue-200'
                          }`}
                          title={`Expected: ${day.expected} | Actual: ${day.actual} | ${
                            day.status === 'red'
                              ? 'Under-staffed'
                              : day.status === 'amber'
                              ? 'Low Coverage'
                              : day.status === 'green'
                              ? 'Optimal Coverage'
                              : 'Over-staffed'
                          }`}
                        />
                        <div className="text-xs text-gray-500">{day.actual}</div>
                      </div>
                    </td>
                  ))}
                  <td className="py-4 pl-6 text-right">
                    <div className="text-sm font-semibold text-gray-900">{channel.weeklyTotal.toLocaleString()}</div>
                    <div className="text-xs text-gray-500">{channel.unit}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Helper text */}
        <div className="mt-4 text-center text-xs text-gray-500">
          Hover over dots for expected vs actual volume • Number below shows actual volume
        </div>

        {/* Key Insights */}
        <div className="mt-6 grid grid-cols-3 gap-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-red-900">Under-staffed Days</span>
              <AlertTriangle className="w-4 h-4 text-red-600" />
            </div>
            <div className="text-2xl font-bold text-red-900">2 days</div>
            <div className="text-xs text-red-700 mt-1">Phone Support: Tue & Wed critical</div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-amber-900">Low Coverage</span>
              <AlertTriangle className="w-4 h-4 text-amber-600" />
            </div>
            <div className="text-2xl font-bold text-amber-900">5 days</div>
            <div className="text-xs text-amber-700 mt-1">Technical team Tue-Wed needs help</div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-green-900">Overall Coverage</span>
              <CheckCircle className="w-4 h-4 text-green-600" />
            </div>
            <div className="text-2xl font-bold text-green-900">86%</div>
            <div className="text-xs text-green-700 mt-1">Most channels well-covered</div>
          </div>
        </div>

        {/* AI Insight */}
        <div className="mt-4 bg-purple-50 border-l-4 border-purple-500 rounded-r-lg p-4">
          <div className="flex items-start space-x-3">
            <TrendingUp className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-semibold text-purple-900">💡 AI Recommendation</h4>
              <p className="text-sm text-purple-800 mt-1">
                Phone Support experienced 10%+ volume spikes on Tuesday and Wednesday, exceeding staffing capacity. Live Chat also showed Thursday spike.
                <span className="font-semibold"> Recommend: Add 2-3 flex agents for mid-week coverage or shift Email team during peak hours.</span>
              </p>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
